import { useEffect, useState } from "react";
import Check from "../../../svg/Check";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import { NextButton } from "../../../components/DizeeComponent/NextButton";
import styled from "styled-components";
import AccessibilityWidget from "../../../components/AccessibilityWidget";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

const DropdownContainer = styled.div`
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  position: relative;
`;

const ActionButton = styled.div`
  font-size: 12px;
  color: white;
  cursor: pointer;
  height: 18px;
  line-height: 18px;
  position: relative;
`;

// TEAM MEMBER ROLES - Artist, Manager, and Support (others hidden for now)
const ROLE_OPTIONS = [
  {
    value: 'artist',
    label: 'Artist',
    description: 'Create and showcase your music, engage with your audience, and grow your brand. Perfect for musicians, singers, and music creators.',
    enabled: true
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Full-featured team member with customizable permissions. Manage the artist\'s career, handle business affairs, and coordinate with other team members.',
    enabled: true
  },
  {
    value: 'support',
    label: 'Support',
    description: 'View-only access to selected apps. Perfect for assistants, interns, or team members who need limited access to help with specific tasks.',
    enabled: true
  }
  // Hidden roles for future expansion:
  // agent, label, publisher, business-manager
];

const Step2 = ({ setStep, setUser, user }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && user.professionRole === "artist") {
      setStep(3);
    } else if (e.key === "Enter" && user.professionRole && ["manager", "support"].includes(user.professionRole)) {
      setStep(15);
    }
  };

  const handleNext = () => {
    if (user.professionRole === "artist") {
      setStep(3);
    } else if (user.professionRole && ["manager", "support"].includes(user.professionRole)) {
      setStep(15); // Team member flow - check for invitations
    }
  };

  const handleRoleSelect = (role) => {
    setUser({ ...user, professionRole: role });
    setDropdownOpen(false);
  };

  const selectedRole = ROLE_OPTIONS.find(role => role.value === user.professionRole);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [user.professionRole]);

  const getFooterText = () => {
    if (selectedRole) {
      return selectedRole.description;
    }
    return "Select the type of account that best suits your role in the music industry.";
  };

  return (
    <Container>
      <FormSection>
        <TypeWriter text={"What describes you best?"} speed={45} />
        
        {/* Role Dropdown - Using Dizee Design Language */}
        <div className="relative mt-[20px] w-[280px]">
          <div 
            className="bg-black rounded-[4px] pr-4 py-3 cursor-pointer flex justify-between items-center text-white text-[12px] transition-all duration-200"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span>{selectedRole ? selectedRole.label : "Select your role..."}</span>
            <div 
              className="w-0 h-0 transition-transform duration-200"
              style={{
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          
          {dropdownOpen && (
            <DropdownContainer 
              className="absolute top-full left-0 right-0 bg-[#111111] rounded-[4px] mt-1 z-50 overflow-hidden shadow-lg max-h-[300px] overflow-y-auto"
            >
              {/* Small spacer at top */}
              <div className="h-2 bg-[#111111]"></div>
              
              <div className="px-[5px] flex flex-col gap-y-[10px]">
                {ROLE_OPTIONS.map((role) => {
                  const isEnabled = role.enabled;
                  const isDisabled = !isEnabled;
                  return (
                    <div
                      key={role.value}
                      className={`flex flex-col gap-y-1 p-3 rounded transition-all duration-200 border ${
                        user.professionRole === role.value 
                          ? 'border-[#FF2300]' 
                          : isDisabled 
                            ? 'border-transparent opacity-40 cursor-not-allowed'
                            : 'border-transparent hover:border-[#FF2300] cursor-pointer'
                      }`}
                      onClick={() => isEnabled && handleRoleSelect(role.value)}
                      style={isDisabled ? { pointerEvents: 'none' } : {}}
                    >
                      <div className="flex items-center gap-2 text-white text-[12px] font-semibold">
                        {role.label}
                        {user.professionRole === role.value && (
                          <Check className="h-[12px] w-[18px]" />
                        )}
                        {isDisabled && (
                          <span className="text-[10px] font-normal opacity-60">(Coming Soon)</span>
                        )}
                      </div>
                      <div className="text-white/70 text-[10px] leading-[1.3]">
                        {role.description}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Small spacer at bottom */}
              <div className="h-2 bg-[#111111]"></div>
            </DropdownContainer>
          )}
        </div>

        <ButtonContainer>
          <ActionButton
            onClick={handleNext}
            className={user.professionRole ? "font-default" : "font-gray"}
          >
            Next
          </ActionButton>
        </ButtonContainer>
      </FormSection>
      
      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          {getFooterText()}
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step2;
